import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bubble } from 'tea-component';
import type { BubbleProps } from 'tea-component';

interface IEllipsisLineClampTextProps {
  text: string;
  className?: string;
  style?: React.CSSProperties;
  lineClamp?: number;
  placement?: BubbleProps['placement'];
  bubbleProps?: Partial<BubbleProps>;
}

const EllipsisLineClampText: React.FC<IEllipsisLineClampTextProps> = ({
  text,
  className,
  style,
  lineClamp = 1,
  placement = 'auto',
  bubbleProps,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const normalizedLineClamp = Math.max(1, Math.floor(lineClamp || 1));

  const textStyle = useMemo<React.CSSProperties>(() => {
    if (normalizedLineClamp > 1) {
      return {
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: normalizedLineClamp,
        ...style,
      };
    }

    return {
      display: 'block',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      ...style,
    };
  }, [normalizedLineClamp, style]);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const measureUnclampedHeight = () => {
      // Measure full height without clamp to avoid false negatives on WebKit.
      const rect = element.getBoundingClientRect();
      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.position = 'fixed';
      clone.style.left = '-99999px';
      clone.style.top = '0';
      clone.style.visibility = 'hidden';
      clone.style.pointerEvents = 'none';
      clone.style.height = 'auto';
      clone.style.maxHeight = 'none';
      clone.style.overflow = 'visible';
      clone.style.textOverflow = 'clip';
      clone.style.display = 'block';
      clone.style.whiteSpace = 'normal';
      clone.style.webkitLineClamp = 'unset';
      clone.style.webkitBoxOrient = 'unset';
      clone.style.width = `${Math.max(rect.width, element.clientWidth)}px`;
      document.body.appendChild(clone);
      const fullHeight = clone.scrollHeight;
      document.body.removeChild(clone);
      return fullHeight;
    };

    const checkOverflow = () => {
      const hasWidthOverflow = element.scrollWidth > element.clientWidth;
      if (normalizedLineClamp <= 1) {
        setIsOverflowing(hasWidthOverflow);
        return;
      }

      const hasHeightOverflow = element.scrollHeight > element.clientHeight + 1;
      const unclampedHeight = measureUnclampedHeight();
      const hasUnclampedHeightOverflow = unclampedHeight > element.clientHeight + 1;
      setIsOverflowing(hasWidthOverflow || hasHeightOverflow || hasUnclampedHeightOverflow);
    };

    checkOverflow();
    if (typeof window !== 'undefined') {
      // Re-check after paint to capture layout/font settling.
      window.requestAnimationFrame(checkOverflow);
    }

    const handleResize = () => checkOverflow();
    window.addEventListener('resize', handleResize);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(element);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, [text, normalizedLineClamp]);

  const textElement = (
    <span ref={textRef} className={className} style={textStyle}>
      {text}
    </span>
  );

  if (!isOverflowing) {
    return textElement;
  }

  return (
    <Bubble
      dark
      closeOnScroll
      placement={placement}
      content={text}
      {...bubbleProps}
    >
      <span style={{ display: 'block', minWidth: 0 }}>
        {textElement}
      </span>
    </Bubble>
  );
};

export default React.memo(EllipsisLineClampText);
